/* global google */

<template>
  <div class="location-form">
    <div class="d-flex justify-content-between align-items-center mb-4">
      <h2 class="mb-0">{{ isEdit ? "Edit Location" : "Add Location" }}</h2>
    </div>
    <b-form @submit.prevent="handleSubmit">
      <b-form-group
        id="name-group"
        label="Location Name"
        label-for="name"
      >
        <b-form-input
          id="name"
          v-model="formData.name"
          required
        ></b-form-input>
      </b-form-group>

      <div class="form-group">
        <label for="address">Address</label>
        <input
          type="text"
          id="address"
          v-model="formData.address"
          required
          class="form-control"
          ref="addressInput"
        />
      </div>

      <b-form-group id="city-group" label="City" label-for="city">
        <b-form-input id="city" v-model="formData.city" disabled></b-form-input>
      </b-form-group>

      <b-form-group id="state-group" label="State" label-for="state">
        <b-form-input id="state" v-model="formData.state" disabled></b-form-input>
      </b-form-group>

      <b-form-group id="country-group" label="Country" label-for="country">
        <b-form-input id="country" v-model="formData.country" disabled></b-form-input>
      </b-form-group>

      <b-form-group id="latitude-group" label="Latitude" label-for="latitude">
        <b-form-input id="latitude" v-model="formData.latitude" disabled></b-form-input>
      </b-form-group>

      <b-form-group id="longitude-group" label="Longitude" label-for="longitude">
        <b-form-input id="longitude" v-model="formData.longitude" disabled></b-form-input>
      </b-form-group>

      <div class="d-flex justify-content-end gap-2 mt-4">
        <b-button variant="secondary" @click="$router.push('/admin/locations')">Cancel</b-button>
        <b-button type="submit" variant="primary">Submit</b-button>
      </div>
    </b-form>
  </div>
</template>

<script>
import { request } from "@/Util/Request";


export default {
  name: "LocationForm",
  props: {
    id: {
      type: [String, Number],
      default: null
    }
  },
  computed: {
    isEdit() {
      return this.$route.name === 'AdminLocationEdit';
    }
  },
  data() {
    return {
      formData: {
        name: "",
        address: "",
        city: "",
        state: "",
        country: "",
        latitude: "",
        longitude: "",
      },
      autocomplete: null,
      showOtherFields: true,
      isSubmitting: false,
      errors: {}
    };
  },
  async created() {
    if (this.isEdit && this.id) {
      await this.fetchLocation();
    }
  },
  mounted() {
    if (window.google && window.google.maps) {
      this.initGooglePlacesAutocomplete();
    } else {
      window.addEventListener("load", () => {
        if (window.google && window.google.maps) {
          this.initGooglePlacesAutocomplete();
        }
      });
    }

    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.classList && node.classList.contains("pac-container")) {
              this.showOtherFields = false;
            }
          });
        }
        if (mutation.removedNodes.length) {
          mutation.removedNodes.forEach((node) => {
            if (node.classList && node.classList.contains("pac-container")) {
              setTimeout(() => {
                this.showOtherFields = true;
              }, 100);
            }
          });
        }
      });
    });

    this.observer.observe(document.body, { childList: true, subtree: true });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    initGooglePlacesAutocomplete() {
      if (!this.$refs.addressInput) return;

      this.autocomplete = new window.google.maps.places.Autocomplete(
        this.$refs.addressInput,
        {
          types: ["address"],
          fields: ["address_components", "geometry", "formatted_address"],
        }
      );

      this.autocomplete.addListener("place_changed", () => {
        const place = this.autocomplete.getPlace();

        if (!place.geometry) {
          return;
        }

        this.formData.address = place.formatted_address;
        this.formData.city = "";
        this.formData.state = "";
        this.formData.country = "";

        for (const component of place.address_components) {
          const componentType = component.types[0];

          switch (componentType) {
            case "locality":
              this.formData.city = component.long_name;
              break;
            case "administrative_area_level_1":
              this.formData.state = component.long_name;
              break;
            case "country":
              this.formData.country = component.long_name;
              break;
          }
        }

        this.formData.latitude = place.geometry.location.lat();
        this.formData.longitude = place.geometry.location.lng();
      });
    },
    async fetchLocation() {
      try {
        const response = await request({
          method: 'get',
          url: `admin/locations/${this.id}`
        });
        
        const location = response.data;
        this.formData = {
          name: location.name || "",
          address: location.address || "",
          city: location.city || "",
          state: location.state || "",
          country: location.country || "",
          latitude: location.latitude || "",
          longitude: location.longitude || ""
        };
      } catch (error) {
        console.error('Error fetching location:', error);
        this.$bvToast.toast('Failed to load location data', {
          title: 'Error',
          variant: 'danger'
        });
      }
    },
    async handleSubmit() {
      console.log("API Call - Submitting Form:", {
        method: this.isEdit ? "PUT" : "POST",
        url: this.isEdit ? `admin/locations/${this.id}` : "admin/locations",
        data: this.formData,
      });

      this.errors = {};
      this.isSubmitting = true;

      try {
        const url = this.isEdit ? `admin/locations/${this.id}` : "admin/locations";
        const method = this.isEdit ? "put" : "post";

        const response = await request({
          method,
          url,
          data: this.formData,
        });

        console.log("API Response - Submit Success:", {
          status: response.status,
          data: response.data,
        });

        this.$bvToast.toast(
          `Location ${this.isEdit ? "updated" : "created"} successfully`,
          {
            title: "Success",
            variant: "success",
          }
        );

        this.$router.push("/admin/locations");
      } catch (error) {
        console.error("API Error - Submit Failed:", {
          status: error.response?.status,
          message: error.response?.data?.message,
          errors: error.response?.data?.errors,
        });

        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else {
          this.$bvToast.toast(
            error.response?.data?.message ||
              `Failed to ${this.isEdit ? "update" : "create"} location`,
            {
              title: "Error",
              variant: "danger",
            }
          );
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
.location-form {
  width: 100%;
  padding: 20px;
}

.gap-2 {
  gap: 0.5rem;
}
</style>

<style>
/* Global styles for Google Places Autocomplete dropdown */
.pac-container {
  z-index: 9999 !important;
}
</style>
